import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface AddEndClientInput {
  name: string;
  email: string;
  job_title: string;
  target_company: string;
  linkedin_url: string;
  notes: string;
}

function trimOrNull(v: string): string | null {
  const s = v.trim();
  return s.length === 0 ? null : s;
}

export function useAddEndClient() {
  const qc = useQueryClient();
  const { companyId } = useBranding();

  return useMutation({
    mutationFn: async (input: AddEndClientInput) => {
      const name = input.name.trim();
      const email = input.email.trim().toLowerCase();

      const { data: existing, error: dupErr } = await supabase
        .from('b2b_end_clients')
        .select('id')
        .eq('b2b_company_id', companyId)
        .eq('email', email)
        .maybeSingle();

      if (dupErr) throw new Error(`b2b_end_clients: ${dupErr.message}`);
      if (existing) {
        throw new Error('A candidate with this email already exists.');
      }

      const userRes = await supabase.auth.getUser();
      const createdBy = userRes.data.user?.id ?? null;

      interface InsertRow {
        b2b_company_id: string;
        name: string;
        email: string;
        client_type: string;
        is_active: boolean;
        created_by?: string;
        job_title?: string;
        target_company?: string;
        linkedin_url?: string;
        notes?: string;
      }

      const row: InsertRow = {
        b2b_company_id: companyId,
        name,
        email,
        client_type: 'neurax',
        is_active: true,
      };
      if (createdBy) row.created_by = createdBy;

      const jobTitle = trimOrNull(input.job_title);
      const targetCompany = trimOrNull(input.target_company);
      const linkedin = trimOrNull(input.linkedin_url);
      const notes = trimOrNull(input.notes);
      if (jobTitle) row.job_title = jobTitle;
      if (targetCompany) row.target_company = targetCompany;
      if (linkedin) row.linkedin_url = linkedin;
      if (notes) row.notes = notes;

      const { data: created, error: insertErr } = await supabase
        .from('b2b_end_clients')
        .insert(row)
        .select()
        .single();

      if (insertErr || !created) {
        throw new Error(`b2b_end_clients: ${insertErr?.message ?? 'insert failed'}`);
      }

      // Best-effort: send candidate the onboarding email (company code + NeuraX download).
      // Failures here don't roll back the candidate row — admin can resend later.
      try {
        await supabase.functions.invoke('send-candidate-onboarding', {
          body: { b2b_end_client_id: (created as { id: string }).id },
        });
      } catch (err) {
        console.warn('send-candidate-onboarding failed (non-fatal):', err);
      }

      return created;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['neurax', 'end-clients'] });
      qc.invalidateQueries({ queryKey: ['neurax', 'dashboard-stats'] });
    },
  });
}
